import * as React from 'react';
import { get } from 'lodash';

type AccordionState = {
  isOpen: boolean;
  // add other fields below
};

type ApplicationState = { lastUpdatedAt: Date; accordions: { [accordionId: string]: AccordionState } };

type LocalItem = {
  [applicationId: string]: ApplicationState;
};

export type StorageKey = { applicationId: string; accordionId: string };

// used to not pollute local storage
const LOCAL_STORAGE_KEY = 'accordion.state';
const LOCAL_STORAGE_APPLICATIONS_LIMIT = 5;
const LOCAL_STORAGE_APPLICATIONS_EXPIRATION = 30;

const defaultState = { isOpen: false };

function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function getAccordionState(
  { applicationId, accordionId }: StorageKey,
  initialState?: AccordionState | null
): AccordionState {
  const localItem = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (localItem) {
    return get(JSON.parse(localItem), `${applicationId}.accordions.${accordionId}`, initialState || defaultState);
  }

  return initialState || defaultState;
}

export function useAccordionState(
  storageKey: StorageKey,
  initialState?: AccordionState | null
): [AccordionState, (updatedState: AccordionState) => void] {
  const [state, setState] = React.useState<AccordionState>(getAccordionState(storageKey, initialState));

  const { applicationId, accordionId } = storageKey;

  // save initial state to local storage on mount
  React.useEffect(() => {
    if (!localStorage.getItem(LOCAL_STORAGE_KEY)) {
      const storageState: LocalItem = {
        [applicationId]: {
          lastUpdatedAt: new Date(),
          accordions: { [accordionId]: { ...state } },
        },
      };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(storageState));
    }
  });

  return [
    state,
    (updatedState: AccordionState) => {
      setState(updatedState);

      const localItem: LocalItem = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)!);
      const updatedLocalItem = {
        ...localItem,
        [applicationId]: {
          lastUpdatedAt: new Date(),
          accordions: { ...localItem[applicationId]?.accordions, [accordionId]: updatedState },
        },
      };

      const applicationIdsByDate = Object.keys(updatedLocalItem).sort((k1, k2) => {
        const a = new Date(updatedLocalItem[k1].lastUpdatedAt);
        const b = new Date(updatedLocalItem[k2].lastUpdatedAt);
        return a > b ? -1 : a === b ? 0 : 1;
      });

      const now = new Date();
      const cleanedLocalItem = applicationIdsByDate
        .filter(
          (k) => now < addDays(new Date(updatedLocalItem[k].lastUpdatedAt), LOCAL_STORAGE_APPLICATIONS_EXPIRATION)
        )
        .slice(0, LOCAL_STORAGE_APPLICATIONS_LIMIT)
        .reduce((accum, key) => {
          accum[key] = updatedLocalItem[key];
          return accum;
        }, {} as LocalItem);

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(cleanedLocalItem));
    },
  ];
}
